import { api } from '@/lib/api'
import type {
  AccountSessionResponse,
  AdminManagedPayoutsResponse,
  AdminRevenuePaymentRow,
  AdminTenantPayoutDetail,
  PaymentsDashboard,
  PayoutAccountPayload,
  PayoutDestination,
  PayoutItemRow,
  ProcessPayoutPayload,
  ProcessPayoutResult,
  RefundPayload,
  RefundRow,
} from './types'

export const paymentsApi = {
  // ── Tenant-side ───────────────────────────────────────────────────
  getDashboard: () =>
    api.get<PaymentsDashboard>('/api/invoicing/payments/dashboard'),

  getPayoutItems: (payoutId: number) =>
    api.get<{ items: PayoutItemRow[] }>(`/api/invoicing/payments/payouts/${payoutId}`),

  getPayoutAccount: () =>
    api.get<{ account: PayoutDestination | null }>('/api/invoicing/payments/payout-account'),

  // Backfill helper for the Stripe-deep-dive section. Walks historical
  // Stripe payments and stamps stripe_fee_cents from the actual
  // balance transaction.
  backfillFees: () =>
    api.post<{ scanned: number; tagged: number; skipped: number }>(
      '/api/invoicing/payments/backfill-fees',
      {},
    ),

  // ── Refunds ───────────────────────────────────────────────────────
  issueRefund: (payload: RefundPayload) =>
    api.post<{ refund: RefundRow }>('/api/invoicing/payments/refund', payload),

  listRefunds: () =>
    api.get<{ refunds: RefundRow[] }>('/api/invoicing/payments/refunds'),

  setPayoutAccount: (payload: PayoutAccountPayload) =>
    api.post<{ account: PayoutDestination | null }>(
      '/api/invoicing/payments/payout-account',
      payload,
    ),

  // ── Embedded Stripe Connect Components (Option B) ─────────────────
  createAccountSession: () =>
    api.post<AccountSessionResponse>(
      '/api/invoicing/stripe-connect/account-session',
      {},
    ),

  // ── Platform-admin ────────────────────────────────────────────────
  adminListPayouts: () =>
    api.get<AdminManagedPayoutsResponse>('/api/admin/managed-payouts'),

  adminGetTenantPayouts: (tenantId: string) =>
    api.get<AdminTenantPayoutDetail>(`/api/admin/managed-payouts/${tenantId}`),

  adminProcessPayout: (tenantId: string, payload: ProcessPayoutPayload) =>
    api.post<ProcessPayoutResult>(
      `/api/admin/managed-payouts/${tenantId}/process`,
      payload,
    ),

  adminUpdateFee: (tenantId: string, bokapsysFeeBps: number) =>
    api.patch<{ ok: true; bokapsysFeeBps: number }>(
      `/api/admin/managed-payouts/${tenantId}/fee`,
      { bokapsysFeeBps },
    ),

  adminGetRevenuePayments: (params: { currency: string; tenantId?: string | null }) => {
    const qs = new URLSearchParams({ currency: params.currency })
    if (params.tenantId) qs.set('tenantId', params.tenantId)
    return api.get<{ payments: AdminRevenuePaymentRow[] }>(
      `/api/admin/managed-payouts/revenue-payments?${qs.toString()}`,
    )
  },
}
